import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  CardContentProps,
  Typography,
  Box,
  styled,
} from '@mui/material';
import type { Theme } from '@mui/system/createTheme';
import { radius } from 'themes/radius';

export type ResourceBaseCardVariant = 'square' | 'horizontal';

export interface ResourceBaseCardProps {
  /** Primary card label. */
  title: string;
  /** Pre-rendered icon/illustration node for the colored section. */
  icon: React.ReactNode;
  /** Background color for the colored media section (and horizontal card body). */
  backgroundColor: string;
  /** Layout variant. */
  variant?: ResourceBaseCardVariant;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Overrides the accessible name (defaults to `title`). */
  ariaLabel?: string;
  testId?: string;
  className?: string;
}

const StyledTitleTypography = styled(Typography, {
  shouldForwardProp: (prop: string) => prop !== 'tileVariant',
})<{ tileVariant: ResourceBaseCardVariant }>(({ tileVariant }) => ({
  // Square tiles are small enough that 2-3 line titles need tighter spacing to fit.
  lineHeight: tileVariant === 'square' ? '18px' : '25px',
  fontWeight: tileVariant === 'horizontal' ? 500 : 'normal',
  width: '100%',
  overflowWrap: 'break-word',
  display: '-webkit-box',
  WebkitLineClamp: tileVariant === 'square' ? 3 : 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const StyledCard = styled(Card, {
  shouldForwardProp: (prop: string) => prop !== 'tileVariant' && prop !== 'selected',
})<{ bgcolor: string; tileVariant: ResourceBaseCardVariant; selected?: boolean }>(
  ({
    theme,
    bgcolor,
    tileVariant,
    selected,
  }: {
    theme: Theme;
    bgcolor: string;
    tileVariant: ResourceBaseCardVariant;
    selected?: boolean;
  }) => ({
    borderRadius: radius.card,
    width: '100%',
    position: 'relative',
    ...(selected && {
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: radius.card,
        // Selected border is shown on desktop only, hidden on mobile/tablet.
        pointerEvents: 'none',
        zIndex: 1,
        [theme.breakpoints.up('md')]: {
          border: `2px solid ${theme.palette.primary.main}`,
        },
      },
    }),
    ...(tileVariant === 'square' && {
      maxWidth: 155,
      height: 144,
      backgroundColor: theme.palette.background.paper,
      [theme.breakpoints.up('sm')]: {
        maxWidth: 170,
        height: 160,
      },
    }),
    ...(tileVariant === 'horizontal' && {
      height: 90,
      backgroundColor: bgcolor,
      boxShadow: 'none',
    }),
  }),
);

const StyledCardActionArea = styled(CardActionArea, {
  shouldForwardProp: (prop: string) => prop !== 'tileVariant',
})<{ tileVariant: ResourceBaseCardVariant }>(
  ({ tileVariant }: { tileVariant: ResourceBaseCardVariant }) => ({
    display: 'flex',
    height: '100%',
    flexDirection: tileVariant === 'square' ? 'column' : 'row-reverse',
    justifyContent: tileVariant === 'square' ? 'flex-start' : 'space-between',
    '& .MuiCardActionArea-focusHighlight': {
      display: 'none',
    },
    '&:hover .MuiCardActionArea-focusHighlight': {
      opacity: 0,
    },
  }),
);

const ColoredSection = styled(Box)<{ tileVariant?: ResourceBaseCardVariant }>(
  ({ tileVariant, theme }) => ({
    width: tileVariant === 'square' ? '100%' : 'fit-content',
    height: tileVariant === 'square' ? 85 : '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: tileVariant === 'square' ? 'center' : 'flex-end',
    [theme.breakpoints.up('sm')]: {
      height: tileVariant === 'square' ? 94 : '100%',
      paddingRight: tileVariant === 'horizontal' ? theme.spacing(1) : 0,
    },
  }),
);

const StyledCardContent = styled(CardContent, {
  shouldForwardProp: (prop: string) => prop !== 'tileVariant',
})<{ tileVariant: ResourceBaseCardVariant } & CardContentProps>(
  ({ theme, tileVariant }: { theme: Theme; tileVariant: ResourceBaseCardVariant }) => ({
    maxHeight: 65,
    overflow: 'hidden',
    padding: 0,
    paddingInline: tileVariant === 'square' ? theme.spacing(1.7) : theme.spacing(2.2),
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: tileVariant === 'square' ? 'center' : 'flex-start',
  }),
);

export const ResourceBaseCard: React.FC<ResourceBaseCardProps> = ({
  title,
  icon,
  backgroundColor,
  variant = 'square',
  onClick,
  disabled = false,
  selected = false,
  ariaLabel,
  testId = 'resource-base-topic-card',
  className = 'resource-base-topic-card',
}) => (
  <StyledCard
    data-testid={testId}
    tileVariant={variant}
    bgcolor={backgroundColor}
    selected={selected}
    className={className}
    sx={{ opacity: disabled ? 0.5 : 1 }}
  >
    <StyledCardActionArea
      tileVariant={variant}
      className={`${className}__action-area`}
      onClick={onClick}
      disabled={disabled}
      disableRipple
      aria-label={ariaLabel || title}
      aria-disabled={disabled}
      aria-pressed={selected}
    >
      <ColoredSection tileVariant={variant} sx={{ backgroundColor }}>
        {icon}
      </ColoredSection>
      <StyledCardContent tileVariant={variant} className={`${className}__content`}>
        <StyledTitleTypography
          tileVariant={variant}
          className={`${className}__title`}
          variant={variant === 'square' ? 'callout' : 'title3'}
        >
          {title}
        </StyledTitleTypography>
      </StyledCardContent>
    </StyledCardActionArea>
  </StyledCard>
);

export default ResourceBaseCard;
